// ─── ToastProvider / useToast ───────────────────────────────────────────────
// A short message that pops up at the bottom of the screen and goes away by
// itself. It replaces the browser's alert() boxes, which stop everything and
// look out of place.
//
// Set up once (already done in App.js):
//   <ToastProvider> …the whole app… </ToastProvider>
//
// Use it in any component:
//   const toast = useToast();
//   toast.error("Couldn't remove this item. Please try again.");
//   toast.success('Kitchen removed');
//   toast.info('Copied to clipboard');
//
//   // With a button, e.g. Undo:
//   toast.show({
//     message: 'Kitchen removed',
//     actionLabel: 'Undo',
//     onAction: () => putKitchenBack(),
//     onExpire: () => reallyDeleteKitchen(),  // runs if the toast closes WITHOUT the button being pressed
//     duration: 6000,
//   });
//
// Only one toast shows at a time. If a new one arrives, the old one closes
// (and its onExpire runs, so nothing waiting on it is lost).

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import Snackbar from '@mui/material/Snackbar';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { CHARCOAL, ERROR, GREEN, bodyFont, labelMono } from '../../../theme/tokens2';

const ToastContext = createContext(null);

const ACCENT = {
  error:   '#F97066', // a lighter red, readable on the dark background
  success: '#6CCB7E',
  info:    'rgba(255,255,255,0.55)',
};

let nextId = 1;

export function ToastProvider({ children }) {
  const [toast, setToast] = useState(null); // the one on screen
  const currentRef = useRef(null);

  // Close the current toast. `pressed` = true when its button was used.
  const finish = useCallback((pressed) => {
    const current = currentRef.current;
    if (!current) return;
    currentRef.current = null;
    setToast(null);
    if (!pressed) current.onExpire?.();
  }, []);

  const show = useCallback((options) => {
    // A new toast replaces the old one; the old one counts as expired.
    if (currentRef.current) finish(false);
    const t = {
      id: nextId++,
      severity: 'info',
      duration: options.actionLabel ? 6000 : 5000,
      ...options,
    };
    currentRef.current = t;
    setToast(t);
    return t.id;
  }, [finish]);

  // Built once, so every component gets the same toast object every time.
  // (A new object on each render would make hooks that depend on it re-run.)
  const api = useRef(null);
  if (!api.current) {
    api.current = {
      show,
      error:   (message, opts) => show({ ...opts, message, severity: 'error' }),
      success: (message, opts) => show({ ...opts, message, severity: 'success' }),
      info:    (message, opts) => show({ ...opts, message, severity: 'info' }),
    };
  }

  // If the whole app closes while a toast with onExpire is open, run it.
  useEffect(() => () => { currentRef.current?.onExpire?.(); }, []);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <Snackbar
        key={toast?.id}
        open={!!toast}
        autoHideDuration={toast?.duration}
        onClose={(_, reason) => {
          if (reason === 'clickaway') return; // clicking elsewhere on the page shouldn't close it
          finish(false);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Box
          role={toast?.severity === 'error' ? 'alert' : 'status'}
          sx={{
            display: 'flex', alignItems: 'center', gap: 2,
            minWidth: { xs: 'auto', sm: 320 }, maxWidth: 520,
            background: CHARCOAL, color: '#fff',
            borderLeft: `3px solid ${ACCENT[toast?.severity] || ACCENT.info}`,
            boxShadow: '0 8px 24px rgba(0,0,0,0.22)',
            pl: 2, pr: 1, py: 1.25,
          }}
        >
          <Typography sx={{ ...bodyFont, fontSize: '13px', lineHeight: 1.5, flex: 1 }}>
            {toast?.message}
          </Typography>

          {toast?.actionLabel && (
            <Box
              component="button"
              type="button"
              onClick={() => { const t = currentRef.current; finish(true); t?.onAction?.(); }}
              sx={{
                ...labelMono, fontSize: '10px',
                color: '#9BE3A8', background: 'none', border: 'none', cursor: 'pointer',
                px: 1.25, py: 0.75, minHeight: 32,
                '&:hover': { color: '#fff' },
                '&:focus-visible': { outline: `2px solid ${GREEN}` },
              }}
            >
              {toast.actionLabel}
            </Box>
          )}

          <Box
            component="button"
            type="button"
            onClick={() => finish(false)}
            aria-label="Dismiss"
            sx={{
              width: 28, height: 28, flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'rgba(255,255,255,0.6)', '&:hover': { color: '#fff' },
              '&:focus-visible': { outline: `2px solid ${ERROR}` },
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </Box>
        </Box>
      </Snackbar>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ref = useContext(ToastContext);
  if (!ref) throw new Error('useToast must be used inside <ToastProvider> (see App.js).');
  return ref.current;
}
